import { useState, useEffect, useCallback } from 'react';
import { getData } from '../global/func';
import { isPrayer } from '../prayers';

// The same countdown the television shows, by the same rules.
//
// Both sides work in minutes since midnight *in the configured city's zone*.
// That is the whole point: the display used to diff a prayer time built in the
// browser's zone against a real instant, so a box set to Cairo and standing in
// Alberta counted down to a moment nine hours off. Neither value here carries a
// zone, so the two cannot disagree about which one they are in.
//
// Lifted out of SettingPreview unchanged when the preview became the home
// screen. It was the most carefully corrected code in the app and none of it
// needed rewriting to be shown differently.

export const minutesOfDay = (hhmm) => {
  const parts = String(hhmm).split(':');
  // Some engines render midnight as "24:00" under hour12:false, which would
  // sort the start of the day after the end of it.
  return (Number(parts[0]) % 24) * 60 + Number(parts[1]);
};

export const minutesNowIn = (tz) => {
  const opts = { hour: '2-digit', minute: '2-digit', hour12: false };
  try {
    return minutesOfDay(new Date().toLocaleTimeString('en', { ...opts, timeZone: tz }));
  } catch {
    // An unknown zone throws a RangeError in Intl. Falling back to the viewer's
    // own clock is wrong, but it is a wrong clock rather than a blank screen.
    return minutesOfDay(new Date().toLocaleTimeString('en', opts));
  }
};

// Which prayer is being waited for, and how long until it. Wraps to tomorrow's
// first entry once the last one has passed, so this is never empty.
export const nextPrayer = (prayers, tz) => {
  const usable = prayers.filter((p) => p.time24);
  if (usable.length === 0) return null;

  const now = minutesNowIn(tz);
  const ahead = usable.find((p) => minutesOfDay(p.time24) > now);

  // Past the last prayer of the day, the next one is tomorrow's first -- at
  // tomorrow's time, which is not today's. Falling back to today's time keeps an
  // older API response working rather than producing NaN.
  const target = ahead ?? usable[0];
  const time = ahead ? target.time24 : (target.tomorrow24 || target.time24);

  let remaining = minutesOfDay(time) - now;
  if (remaining < 0) remaining += 24 * 60;

  return { prayer: target.prayer, remaining, at: target.today, tomorrow: !ahead };
};

export const formatRemaining = (minutes) =>
  minutes <= 0 ? 'now'
    : minutes >= 60 ? `${Math.floor(minutes / 60)}h ${minutes % 60}m`
      : `${minutes}m`;

/**
 * Today's prayer times for a device, and which one is next.
 *
 * Refetches whenever any setting that changes the answer changes -- as one
 * derived string, not a list of individual dependencies. calc-source was once
 * left out of such a list, so switching the time source changed the setting,
 * never refetched, and looked as though it had not taken. The linter cannot
 * check an array of `settings?.['x']` expressions; it can check this.
 */
export const usePrayerTimes = (deviceKey, settings) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Bumped on the minute so the countdown re-renders. The value is never read;
  // changing it is the point.
  const [, setTick] = useState(0);

  useEffect(() => {
    // Line up with the wall clock first, then keep to the minute, so the number
    // changes when the minute does rather than 40 seconds later.
    let interval;
    const timeout = setTimeout(() => {
      setTick((n) => n + 1);
      interval = setInterval(() => setTick((n) => n + 1), 60 * 1000);
    }, (60 - new Date().getSeconds()) * 1000);

    return () => { clearTimeout(timeout); if (interval) clearInterval(interval); };
  }, []);

  const previewKey = [
    settings?.['location'],
    settings?.['calc-source'],
    settings?.['calc-method'],
    settings?.['asr-method'],
    settings?.['high-lat-adjust'],
  ].join('|');

  const refresh = useCallback(async () => {
    const locationId = settings?.['location'];
    if (!locationId || !deviceKey) return;

    setLoading(true);
    setError(null);

    try {
      const auth = {
        DEVICE_KEY: deviceKey,
        SESSION_ID: import.meta.env.REACT_APP_SESSION_ID,
      };
      const url = encodeURI(
        `${import.meta.env.REACT_APP_API_SERVER_URL}/prayers/preview.php?location_id=${locationId}`
      );
      const data = await getData(url, auth);
      setPreview(data?.[0] ?? null);
    } catch {
      setError('Could not load prayer times. Check your connection.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceKey, previewKey]);

  useEffect(() => { refresh(); }, [refresh]);

  const upcoming = preview?.prayers
    ? nextPrayer(preview.prayers.filter((p) => isPrayer(p.prayer)), preview.location?.tz)
    : null;

  return { preview, upcoming, loading, error, refresh };
};
